/**
 * useStageResolvedEditValues — the pending property-value draft for a property
 * update action.
 *
 * Holds raw values (string, number, object, null) exactly as they will be sent
 * back in the payload, with one normalisation: a stage/lifecycle property may
 * arrive as a stage *name* (`ahoy_negotiation`) while every stage editor and
 * label speaks entity_ids, so names are resolved as soon as the pipeline
 * stages load — which is usually after first render.
 *
 * Owning the draft here keeps the initialisation and the late stage resolution
 * in one place, so both apply the same name→entity_id mapping. When they
 * drifted, a card could open showing a raw stage name in a field whose editor
 * only speaks entity_ids.
 *
 * Change detection lives with the autosave (`useCoordinatedAutoSave`), which
 * compares the built payload against the last one it persisted — there is no
 * second dirty flag here to disagree with it.
 */

import { useEffect, useRef, useState } from 'react';

import type { Stage } from '@/lib/crm/hooks/useStages';

import { extractValue } from './shared/utils';
import { resolveStageEntityId } from './stageUtils';
import { isStageProperty } from './UpdateActionEditForm.helpers';

interface UseStageResolvedEditValuesOptions {
  propertyKeys: string[];
  proposedProperties: Record<string, unknown>;
  stages: Stage[];
}

export interface StageResolvedEditValues {
  /** The pending draft, keyed by property type name. */
  editValues: Record<string, unknown>;
  setEditValues: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
}

/** Payload value for one property, with stage names resolved to entity_ids. */
function initialValueFor(
  key: string,
  proposedProperties: Record<string, unknown>,
  stages: Stage[]
): unknown {
  const raw = extractValue(proposedProperties[key]);
  if (
    isStageProperty(key) &&
    typeof raw === 'string' &&
    raw &&
    stages.length > 0
  ) {
    return resolveStageEntityId(raw, stages);
  }
  return raw;
}

export function useStageResolvedEditValues({
  propertyKeys,
  proposedProperties,
  stages,
}: UseStageResolvedEditValuesOptions): StageResolvedEditValues {
  const [editValues, setEditValues] = useState<Record<string, unknown>>(() => {
    const init: Record<string, unknown> = {};
    for (const key of propertyKeys) {
      init[key] = initialValueFor(key, proposedProperties, stages);
    }
    return init;
  });

  // Resolve stage names → entity_ids when stages data loads after first render.
  const stageResolved = useRef(false);
  useEffect(() => {
    if (stageResolved.current || stages.length === 0) {
      return;
    }

    const updates: Record<string, string> = {};
    for (const key of propertyKeys) {
      if (!isStageProperty(key)) {
        continue;
      }
      const val = editValues[key];
      if (!val || typeof val !== 'string') {
        continue;
      }
      if (stages.some(s => s.entity_id === val)) {
        continue;
      }
      const resolved = resolveStageEntityId(val, stages);
      if (resolved !== val) {
        updates[key] = resolved;
      }
    }

    stageResolved.current = true;
    if (Object.keys(updates).length > 0) {
      setEditValues(prev => ({ ...prev, ...updates }));
    }
  }, [stages, propertyKeys, editValues]);

  return { editValues, setEditValues };
}
